import { motion } from "framer-motion";
import { Clock, Star, ArrowRight } from "lucide-react";
import { IMAGES } from "@/data";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Hero() {
  return (
    <section id="home" className="relative pt-32 pb-20 md:pt-40 md:pb-28 px-5 md:px-10 overflow-hidden">
      {/* decorative blobs */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-amber-200/40 rounded-full blur-3xl" />
      <div className="absolute top-40 -left-24 w-80 h-80 bg-sky-200/40 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 bg-white border border-sky-100 text-sky-600 font-semibold text-sm px-4 py-2 rounded-full shadow-sm">
            <Clock className="w-4 h-4" /> Open 24 Hours · Ages 6 weeks – 12 years
          </span>
          <h1 className="mt-6 text-5xl sm:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Safe, Nurturing &{" "}
            <span className="relative inline-block text-sky-500">
              Joyful
              <svg className="absolute -bottom-2 left-0 w-full" height="12" viewBox="0 0 200 12" fill="none">
                <path d="M2 9C50 3 150 3 198 9" stroke="#FBBF24" strokeWidth="5" strokeLinecap="round" />
              </svg>
            </span>{" "}
            Childcare
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
            Welcome to Blessed Beginnings Learning Center — a warm home-based program in
            Cincinnati offering reliable, around-the-clock care with small group sizes
            so every child gets the attention they deserve.
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <button
              data-testid="hero-schedule-tour-button"
              onClick={() => scrollTo("contact")}
              className="group inline-flex items-center gap-2 bg-sky-400 hover:bg-sky-500 text-white font-bold py-3.5 px-8 rounded-full shadow-[0_4px_14px_0_rgba(56,189,248,0.39)] hover:-translate-y-1 transition-all duration-200"
            >
              Schedule a Tour
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-programs-button"
              onClick={() => scrollTo("programs")}
              className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-800 font-bold py-3.5 px-8 rounded-full border border-slate-200 hover:-translate-y-1 transition-all duration-200"
            >
              Explore Our Program
            </button>
          </div>

          <div className="mt-8 flex items-center gap-2 text-slate-600">
            <div className="flex text-amber-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-amber-400" />
              ))}
            </div>
            <span className="font-semibold">Step Up to Quality Rated Program</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl rotate-1">
            <img
              src={IMAGES.heroMain}
              alt="Children playing and learning together"
              className="w-full h-[420px] md:h-[500px] object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-4 w-40 h-40 md:w-52 md:h-52 rounded-3xl overflow-hidden shadow-xl border-4 border-white -rotate-3">
            <img src={IMAGES.heroSmall1} alt="Child drawing" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -top-5 -right-3 bg-white rounded-2xl shadow-xl px-5 py-3 rotate-2">
            <p className="font-accent text-2xl text-sky-500 leading-none">Small ratios,</p>
            <p className="font-accent text-2xl text-amber-500 leading-none">big hearts!</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
